/**
 * RoSelect - 下拉选择框 Web 组件
 *
 * 功能特性：
 * - 支持四种状态：待选择、选中(展开)、校验失败、禁用
 * - 可选浮动标签效果（仅在有选中值时上浮）
 * - 尺寸 size：large | medium | small（默认 medium；非法值按 medium；float-label 时强制 large 视觉）；`disable-adaptation="true"` 关闭窄屏高度降档
 * - 支持前置/后置图标（slot：prefix-icon、suffix-icon）
 * - 可配置可清空（clearable）：有选中值时可清空为无值（与 reset() 不同：不恢复 default-value，且派发 change）；清除图标不占常态布局，类 Element UI——在精细指针且支持悬停的设备上仅悬停/聚焦触发条时显示；触屏等降级为常显。无障碍文案可配 clear-aria-label，键盘聚焦触发条内时通过 :focus-within 仍可操作清除
 * - 可配置是否展示右侧下拉箭头（show-arrow）
 * - 可配置是否启用下拉内搜索过滤（searchable）；搜索框在下拉面板顶部、选项列表上方；占位符用 search-placeholder，未传则用 placeholder
 * - 下拉展开方向：自动向剩余空间更大的一侧展开（上或下）
 * - 可配置下拉面板最大高度（max-height 属性）：不设默认 280px；设为 0 时占满展开方向剩余空间
 * - 默认 portal：下拉面板传送到 body，在弹窗内不被裁切；portal="false" 可关闭
 * - 触发器选中文案过长时右侧渐变遮罩（设计稿 266-26654）；可配置默认值（default-value）：初始值 + reset() 恢复
 * - 选项来源：优先子节点 <option>，否则 options 属性（JSON 数组）；可用 exclude-values 排除特定 option 值
 * - 必填校验、完整事件与表单集成（Form-Associated）；必填文案：error-message 或 window.RoFormLocale.required（见 snippets/ro-form-locale.liquid）；通用 UI 文案见 window.RoComponentsLocale（snippets/ro-components-locale.liquid）
 * - 键盘导航与无障碍（ARIA）
 *
 * 使用方法：
 * 1. 基础用法（options 属性）：
 *    <ro-select placeholder="请选择" options='[{"value":"a","label":"选项 A"},{"value":"b","label":"选项 B"}]'></ro-select>
 *
 * 2. 使用子节点 option：
 *    <ro-select placeholder="请选择国家">
 *      <option value="us">United States</option>
 *      <option value="uk">United Kingdom</option>
 *    </ro-select>
 *
 * 3. 浮动标签 + 必填：
 *    <ro-select placeholder="国家 *" float-label required options='[...]'></ro-select>
 *
 * 4. 带图标与箭头配置：
 *    <ro-select placeholder="搜索" show-arrow>
 *      <svg slot="prefix-icon" width="24" height="24">...</svg>
 *    </ro-select>
 *    <ro-select placeholder="选择" show-arrow="false">...</ro-select>
 *
 * 5. 关闭下拉内搜索：
 *    <ro-select placeholder="选择" searchable="false" options='[...]'></ro-select>
 *
 * 6. 默认值（default-value）：未设置 value 时作为初始值，reset() 时恢复为该值
 *    <ro-select placeholder="国家" default-value="cn" options='[...]'></ro-select>
 *
 * 7. 下拉内搜索占位符（search-placeholder，未传则用 placeholder）：
 *    <ro-select placeholder="国家" search-placeholder="搜索国家..." options='[...]'></ro-select>
 *
 * 8. 尺寸档位（large | medium | small，默认 medium；非法值按 medium；float-label 时强制 large 视觉）：
 *    <ro-select size="small" placeholder="紧凑" options='[...]'></ro-select>
 *
 * 9. 可清空（clearable="true"，关闭用 clearable="false"）：
 *    <ro-select clearable value="a" options='[{"value":"a","label":"A"}]' clear-aria-label="清除所选"></ro-select>
 *
 * 10. 监听事件与调用方法：
 *    select.addEventListener('change', e => console.log(e.detail.value));
 *    const result = select.validate();
 *    select.reset(); select.focus(); select.clearValue();
 */
class RoSelect extends HTMLElement {
  static formAssociated = true;

  /**
   * 仅规范非空 size；空值由 `_syncSizeAttribute` 写回 medium。非法值 → medium。
   * 设计稿节点：大 417-59551、中 265-26428、小 265-26508（数值以 Figma dev 为准时可微调下方 CSS）
   */
  static _normalizeSize(raw) {
    const s = String(raw).trim().toLowerCase();
    if (s === "large" || s === "medium" || s === "small") return s;
    return "medium";
  }

  static get PORTAL_STYLES() {
    return `
.ro-select-portal { position: fixed; left: 0; top: 0; width: 0; height: 0; z-index: 9999; pointer-events: none; }
.ro-select-portal-in-dialog { position: absolute; left: 0; top: 0; right: 0; bottom: 0; width: 100%; height: 100%; pointer-events: none; }
.ro-select-portal > * { pointer-events: auto; }
.ro-select-portal .dropdown-panel {
  display: flex;
  position: fixed;
  flex-direction: column;
  background: var(--bg-white-color, #fff);
  border: 1px solid var(--border-color, #e1e1e1);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-height: 280px;
  overflow: hidden;
  padding: 8px 0;
}
.ro-select-portal.no-search .dropdown-search { display: none; }
.ro-select-portal .dropdown-search {
  display: flex; align-items: center; gap: 8px;
  margin: 8px 16px; padding: 12px 16px; min-height: 40px; box-sizing: border-box;
  background: var(--bg-white-color, #fff);
  border: 1px solid var(--border-color, #e1e1e1); border-radius: 4px;
  flex-shrink: 0;
}
.ro-select-portal .dropdown-search:focus-within {
  border-color: var(--main-color, #161616);
}
.ro-select-portal .dropdown-search-icon {
  display: flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; flex-shrink: 0;
  color: var(--second-color, #6c6d6e);
}
.ro-select-portal .dropdown-search .search-input {
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  color: var(--main-color, #161616);
  font-weight: 400; font-size: 14px; line-height: 20px;
  padding: 0; margin: 0; box-sizing: border-box;
}
.ro-select-portal .dropdown-search .search-input::placeholder {
  color: var(--second-color, #6c6d6e);
}
.ro-select-portal .dropdown-search .search-input:focus:not(:focus-visible) {
  outline: none;
  box-shadow: none;
}
.ro-select-portal .dropdown-search .search-input:focus-visible {
  outline: none;
  box-shadow: none;
}
.ro-select-portal .option-list {
  flex: 1; min-height: 0; overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--ro-select-scrollbar-thumb, #d1d1d1) transparent;
}
.ro-select-portal .option-list::-webkit-scrollbar { width: 6px; }
.ro-select-portal .option-list::-webkit-scrollbar-track { background: transparent; border-radius: 3px; }
.ro-select-portal .option-list::-webkit-scrollbar-thumb {
  background: var(--ro-select-scrollbar-thumb, #d1d1d1); border-radius: 3px;
}
.ro-select-portal .option-list::-webkit-scrollbar-thumb:hover {
  background: var(--ro-select-scrollbar-thumb-hover, #b8b8b8);
}
.ro-select-portal .option-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-weight: 400; font-size: 14px; line-height: 20px;
  color: var(--main-color, #161616); cursor: pointer;
  transition: background 0.15s ease;
}
.ro-select-portal .option-item:hover { background: rgba(0,0,0,0.04); }
.ro-select-portal .option-item.selected {
  background: transparent; color: var(--brand-color, #EA0029); font-weight: 600;
}
.ro-select-portal .option-item[aria-disabled="true"] { opacity: 0.5; cursor: not-allowed; }

/* 多层级动态垂直间距 */
.ro-select-portal .dropdown-panel.depth-2 .option-item.level-1,
.ro-select-portal .dropdown-panel.depth-3 .option-item.level-1 {
  padding-top: 12px;
  padding-bottom: 12px;
}
.ro-select-portal .dropdown-panel.depth-2 .option-item.level-2 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.ro-select-portal .dropdown-panel.depth-3 .option-item.level-2,
.ro-select-portal .dropdown-panel.depth-3 .option-item.level-3 {
  padding-top: 8px;
  padding-bottom: 8px;
}

/* 多层级缩进逻辑 */
.ro-select-portal .option-item.level-1 { padding-left: 24px; }
.ro-select-portal .option-item.level-2 { padding-left: 40px; }
.ro-select-portal .option-item.level-3 { padding-left: 52px; }

@media (max-width: 1183px) {
  .ro-select-portal .option-item.level-1 { padding-left: 16px; }
  .ro-select-portal .option-item.level-2 { padding-left: 28px; }
  .ro-select-portal .option-item.level-3 { padding-left: 40px; }
}

.ro-select-portal .option-group-header {
  font-weight: 500;
  color: var(--main-color, #161616);
}
.ro-select-portal .option-label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ro-select-portal .group-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  transition: transform 0.2s ease;
  color: var(--main-color, #161616);
}
.ro-select-portal .option-item.expanded .group-arrow {
  transform: rotate(180deg);
}

.ro-select-portal-in-dialog .dropdown-panel { position: absolute; }
.ro-select-portal .option-empty {
  padding: 16px; text-align: center;
  color: var(--second-color, #6c6d6e); font-size: 14px; line-height: 20px;
}
`;
  }

  /** 构造：挂载 Shadow、表单关联、实例状态与事件绑定 */
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._internals = this.attachInternals();
    this._value = "";
    this._options = [];
    this._expandedKeys = new Set(); // 记录展开的分组路径
    this._lastSyncedDatasetKeys = []; // 用于 _syncValueElDataset 清理旧 data-* 键
    this._isOpen = false;
    this._portalUsed = false;
    this._portalRoot = null;
    this._boundUpdatePortalPosition = this._updatePortalPosition.bind(this);
    this._isError = false;
    this._errorMessage = "";
    this._searchQuery = "";
    /** 本次展开是否由键盘（Enter/Space）触发，用于搜索框 focus 是否显示聚焦环 */
    this._openViaKeyboard = false;
    this._boundCloseOnPointerDownOutside = this._closeOnClickOutside.bind(this);
    this._boundCloseOnClickOutside = this._closeOnClickOutside.bind(this);
    this._boundKeydown = this._onKeydown.bind(this);
    this._boundTriggerClick = (e) => this._onTriggerClick(e);
    /** 清除按钮点击：拦截冒泡，避免触发展开/收起 */
    this._boundClearClick = (e) => this._clearSelection(e);
    this._render();
  }

  /** 首次渲染：生成 shadow 模板并缓存容器、标签、面板、搜索框等 DOM 引用 */
  _render() {
    const triggerId = `ro-select-trigger-${Math.random().toString(36).slice(2, 9)}`;
    const listboxId = `ro-select-listbox-${Math.random().toString(36).slice(2, 9)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
          position: relative;
          --ro-field-padding: 12px 16px;
        }

        /* 主触发条：内边距统一用 --ro-field-padding；各档只改高度 / gap */
        .select-container {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: var(--ro-field-padding);
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 4px;
          transition: border-color 0.2s ease;
          cursor: pointer;
          box-sizing: border-box;
        }
        :host([size="large"]) .select-container {
          height: 56px;
        }
        /* medium（默认）：总高 48（设计稿「中」265-26428） */
        :host([size="medium"]) .select-container,
        :host(:not([size])) .select-container {
          height: 48px;
        }
        /* small：总高 44（设计稿「小」265-26508） */
        :host([size="small"]) .select-container {
          height: 44px;
          gap: 8px;
        }
        /* 浮动标签固定使用 large 档位尺寸 */
        :host([float-label]) .select-container {
          height: 56px;
          gap: 12px;
        }

        :host([focused]) .select-container {
          border-color: var(--main-color, #161616);
        }

        :host([error]) .select-container {
          border-color: var(--error-color, #d93840);
        }

        :host([disabled]) .select-container {
          background: var(--ro-select-disabled-bg, rgba(225, 225, 225, 0.4));
          cursor: not-allowed;
        }

        .icon-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
        }
        .icon-slot.empty { display: none; }
        .icon-slot::slotted(*) {
          width: 24px;
          height: 24px;
          display: block;
        }

        .select-wrapper {
          flex: 1;
          min-width: 0;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }
        :host([size="large"]) .select-wrapper { min-height: 32px; }
        :host([float-label]) .select-wrapper { min-height: 30px; }
        :host([size="medium"]) .select-wrapper,
        :host(:not([size])) .select-wrapper { min-height: 24px; }
        :host([size="small"]) .select-wrapper { min-height: 20px; }
        /* 设计稿 266-26654：内容超出时右侧渐变遮罩（透明→背景色），非省略号 */
        .select-wrapper::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 32px;
          background: linear-gradient(to right, transparent, var(--bg-white-color, #fff));
          pointer-events: none;
          z-index: 1;
        }
        :host([disabled]) .select-wrapper::after {
          background: none;
        }

        :host([float-label][has-value]) .select-wrapper {
          justify-content: flex-start;
        }

        .label {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          color: var(--second-color, #6c6d6e);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        /* 非浮动模式：有选中值时只显示 value-text，隐藏 placeholder */
        :host(:not([float-label])[has-value]) .label {
          display: none;
        }
        :host([float-label][has-value]) .label {
          position: static;
          transform: none;
          font-size: 12px;
          line-height: 16px;
        }

        .value-text {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          color: var(--main-color, #161616);
          font-weight: 400;
          font-size: 16px;
          line-height: 20px;
          padding: 0;
          margin: 0;
          text-align: left;
          pointer-events: none;
          white-space: nowrap;
          min-width: 0;
        }
        :host([size="medium"]) .value-text,
        :host(:not([size])) .value-text,
        :host([size="small"]) .value-text {
          font-size: 14px;
          line-height: 20px;
        }
        :host([float-label]) .value-text {
          font-size: 16px;
          line-height: 22px;
        }
        :host([disabled]) .value-text {
          color: var(--second-color, #6c6d6e);
        }

        /* 下拉箭头与清除按钮共用 24×24 槽位：清除展示时叠在箭头上（类 Element UI） */
        .arrow-stack {
          position: relative;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }
        /* 隐藏箭头时：与旧版一致不占位，除非可清空且已有值（需保留清除点击区） */
        :host([show-arrow="false"]) .arrow-stack {
          display: none;
        }
        :host([show-arrow="false"][clearable]:not([clearable="false"])[has-value]) .arrow-stack {
          display: block;
        }
        .arrow-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          color: var(--second-color, #6c6d6e);
        }
        :host([show-arrow="false"]) .arrow-wrap {
          display: none;
        }
        :host([focused]) .arrow-wrap {
          transform: rotate(180deg);
        }

        /* 可清空：绝对定位于箭头槽内；默认不占第二列宽度 */
        .clear-btn-wrap {
          position: absolute;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .clear-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          width: 24px;
          height: 24px;
          padding: 0;
          margin: 0;
          border: none;
          background: transparent;
          border-radius: 50%;
          cursor: pointer;
          color: var(--second-color, #6c6d6e);
          flex-shrink: 0;
        }
        /* 悬停 / 键盘聚焦到按钮时显示圆形灰底 */
        .clear-btn:hover,
        .clear-btn:focus-visible {
          background: #e5e5e5;
          color: var(--main-color, #161616);
        }
        .clear-btn:disabled {
          cursor: not-allowed;
          opacity: 0.45;
        }
        .clear-btn svg {
          display: block;
          width: 16px;
          height: 16px;
        }

        /**
         * 触屏 / 无悬停：有资格则常显清除，避免无法点击。
         * 精细指针 + 悬停：仅 hover / focus-within 触发条时显示（Tab 进入子控件亦满足 focus-within）。
         */
        @media (hover: none), (pointer: coarse) {
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .clear-btn-wrap {
            display: flex;
          }
          /* 常显清除时隐去下层箭头，避免叠影 */
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .arrow-wrap {
            opacity: 0;
          }
        }
        @media (hover: hover) and (pointer: fine) {
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .select-container:hover .clear-btn-wrap,
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .select-container:focus-within .clear-btn-wrap {
            display: flex;
          }
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .select-container:hover .arrow-wrap,
          :host([clearable]:not([clearable="false"])[has-value]:not([disabled])) .select-container:focus-within .arrow-wrap {
            opacity: 0;
          }
        }

        .error-message {
          display: none;
          margin-top: 8px;
          padding: 0 16px;
          color: var(--error-color, #d93840);
          font-weight: 400;
          font-size: var(--error-font-size, 12px);
          line-height: var(--error-line-height, 16px);
        }
        :host([error]) .error-message { display: block; }

        .dropdown-panel {
          display: none;
          position: absolute;
          left: 0;
          right: 0;
          top: 100%;
          margin-top: 4px;
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          z-index: 1000;
          max-height: 280px;
          overflow: hidden;
          flex-direction: column;
        }
        :host([focused]) .dropdown-panel {
          display: flex;
        }
        :host([expand-up]) .dropdown-panel {
          top: auto;
          bottom: 100%;
          margin-top: 0;
          margin-bottom: 4px;
        }

        /* 下拉内搜索：设计稿 423-87454 - 白底、浅灰边框、圆角、充足内边距、左侧图标 */
        .dropdown-search {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 12px 12px 0;
          padding: 10px 16px;
          min-height: 40px;
          box-sizing: border-box;
          background: var(--bg-white-color, #fff);
          border: 1px solid var(--border-color, #e1e1e1);
          border-radius: 4px;
          flex-shrink: 0;
        }
        .dropdown-search:focus-within {
          border-color: var(--main-color, #161616);
        }
        :host([searchable="false"]) .dropdown-search {
          display: none;
        }
        .dropdown-search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          color: var(--second-color, #6c6d6e);
        }
        .dropdown-search .search-input {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          background: transparent;
          color: var(--main-color, #161616);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }
        .dropdown-search .search-input::placeholder {
          color: var(--second-color, #6c6d6e);
        }
        /* 搜索框聚焦态由父级 .dropdown-search:focus-within 承载，避免 input 自身出现额外 outline */
        .dropdown-search .search-input:focus:not(:focus-visible) {
          outline: none;
          box-shadow: none;
        }
        .dropdown-search .search-input:focus-visible {
          outline: none;
          box-shadow: none;
        }

        /* 下拉选项列表：与设计稿一致，左右 12px 与搜索区对齐；flex 占满面板剩余高度 */
        .option-list {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 12px 0 12px 0;
          margin: 0 12px 12px 12px;
          /* 滚动条与设计稿 423-87608 一致：细窄、圆角、灰轨+灰 thumb */
          scrollbar-width: thin;
          scrollbar-color: var(--ro-select-scrollbar-thumb, #d1d1d1) transparent;
        }
        .option-list::-webkit-scrollbar {
          width: 6px;
        }
        .option-list::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 3px;
        }
        .option-list::-webkit-scrollbar-thumb {
          background: var(--ro-select-scrollbar-thumb, #d1d1d1);
          border-radius: 3px;
        }
        .option-list::-webkit-scrollbar-thumb:hover {
          background: var(--ro-select-scrollbar-thumb-hover, #b8b8b8);
        }
        .option-item {
          padding: 10px 16px;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          color: var(--main-color, #161616);
          cursor: pointer;
          transition: background 0.15s ease;
          display: flex;
          align-items: center;
        }
        .option-item:hover {
          background: rgba(0,0,0,0.04);
        }
        /* 选中项：红色加粗文字，无整行背景（与设计稿 407-138718 一致） */
        .option-item.selected {
          background: transparent;
          color: var(--brand-color, #EA0029);
          font-weight: 600;
        }
        .option-item[aria-disabled="true"] {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* 多层级动态垂直间距 */
        .dropdown-panel.depth-2 .option-item.level-1,
        .dropdown-panel.depth-3 .option-item.level-1 {
          padding-top: 12px;
          padding-bottom: 12px;
        }
        .dropdown-panel.depth-2 .option-item.level-2 {
          padding-top: 10px;
          padding-bottom: 10px;
        }
        .dropdown-panel.depth-3 .option-item.level-2,
        .dropdown-panel.depth-3 .option-item.level-3 {
          padding-top: 8px;
          padding-bottom: 8px;
        }

        /* 多层级缩进逻辑 */
        .option-item.level-1 { padding-left: 24px; }
        .option-item.level-2 { padding-left: 40px; }
        .option-item.level-3 { padding-left: 52px; }

        @media (max-width: 1183px) {
          .option-item.level-1 { padding-left: 16px; }
          .option-item.level-2 { padding-left: 28px; }
          .option-item.level-3 { padding-left: 40px; }
        }

        .option-group-header {
          font-weight: 500;
          color: var(--main-color, #161616);
        }
        .option-label {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .group-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          margin-left: 8px;
          transition: transform 0.2s ease;
          color: var(--main-color, #161616);
        }
        .option-item.expanded .group-arrow {
          transform: rotate(180deg);
        }

        .option-empty {
          padding: 16px;
          text-align: center;
          color: var(--second-color, #6c6d6e);
          font-size: 14px;
          line-height: 20px;
        }

        @media (max-width: 1183px) {
          :host(:not([disable-adaptation="true"])[size="large"]) .select-container {
            height: 48px;
            gap: 12px;
          }
          :host(:not([disable-adaptation="true"])[size="medium"]) .select-container,
          :host(:not([disable-adaptation="true"]):not([size])) .select-container {
            height: 44px;
            gap: 8px;
          }
          :host(:not([disable-adaptation="true"])[float-label]) .select-container {
            height: 48px;
            gap: 12px;
          }
          :host(:not([disable-adaptation="true"])[size="large"]) .select-wrapper {
            min-height: 24px;
          }
          :host(:not([disable-adaptation="true"])[size="medium"]) .select-wrapper,
          :host(:not([disable-adaptation="true"]):not([size])) .select-wrapper {
            min-height: 20px;
          }
          :host(:not([disable-adaptation="true"])[float-label]) .select-wrapper {
            min-height: 24px;
          }
          .error-message { padding: 0 12px; }
        }
      </style>

      <div class="select-container" id="${triggerId}" role="combobox" aria-expanded="false" aria-haspopup="listbox" aria-controls="${listboxId}" tabindex="0">
        <slot name="prefix-icon" class="icon-slot"></slot>
        <div class="select-wrapper">
          <span class="label"></span>
          <div class="value-text"></div>
        </div>
        <slot name="suffix-icon" class="icon-slot"></slot>
        <div class="arrow-stack">
          <div class="arrow-wrap" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 10L12 15L17 10" stroke="#161616" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="clear-btn-wrap" part="clear-wrap" aria-hidden="true">
            <button type="button" class="clear-btn" part="clear-button" tabindex="0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M12.6665 4L4.6665 12" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
                <path d="M4.6665 4L12.6665 12" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="dropdown-panel" id="${listboxId}" role="listbox">
        <div class="dropdown-search">
          <span class="dropdown-search-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3.6" y="3.6" width="14.8" height="14.8" rx="7.4" stroke="#161616" stroke-width="1.2"/>
              <path d="M16 16L20 20" stroke="#161616" stroke-width="1.2"/>
            </svg>
          </span>
          <input type="text" class="search-input" aria-label="搜索选项" />
        </div>
        <div class="option-list"></div>
      </div>
      <div class="error-message"></div>
    `;

    this._containerEl = this.shadowRoot.querySelector(".select-container");
    this._labelEl = this.shadowRoot.querySelector(".label");
    this._valueEl = this.shadowRoot.querySelector(".value-text");
    this._errorEl = this.shadowRoot.querySelector(".error-message");
    this._panelEl = this.shadowRoot.querySelector(".dropdown-panel");
    this._searchInputEl = this.shadowRoot.querySelector(".search-input");
    this._optionListEl = this.shadowRoot.querySelector(".option-list");
    this._prefixSlot = this.shadowRoot.querySelector('slot[name="prefix-icon"]');
    this._suffixSlot = this.shadowRoot.querySelector('slot[name="suffix-icon"]');
    this._clearBtnWrap = this.shadowRoot.querySelector(".clear-btn-wrap");
    this._clearBtn = this.shadowRoot.querySelector(".clear-btn");
  }

  /** 读取 default-value 属性 */
  _getDefaultValueAttr() {
    return String(this.getAttribute("default-value") ?? "").trim();
  }

  /** 挂载：解析选项、同步默认值、绑定点击/键盘/搜索/ slotchange */
  connectedCallback() {
    this._syncSizeAttribute();
    this._buildOptions();
    if (!this.getAttribute("value") || this.getAttribute("value") === "") {
      const def = this._getDefaultValueAttr();
      if (def !== "") this.setAttribute("value", def);
    }
    this._updateLabel();
    this._updateSearchPlaceholder();
    this._syncSearchAriaLabel();
    this._updateDisplay();
    this._updateSlotVisibility(this._prefixSlot);
    this._updateSlotVisibility(this._suffixSlot);

    this._containerEl.addEventListener("click", this._boundTriggerClick);
    this._containerEl.addEventListener("keydown", this._boundKeydown);
    this._searchInputEl.addEventListener("input", (e) => {
      this._searchQuery = e.target.value.trim();
      this._renderOptions();
    });
    this._searchInputEl.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Escape") this._close();
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this._focusNextOption(e.key === "ArrowDown" ? 1 : -1);
      }
    });

    this._prefixSlot.addEventListener("slotchange", () => this._updateSlotVisibility(this._prefixSlot));
    this._suffixSlot.addEventListener("slotchange", () => this._updateSlotVisibility(this._suffixSlot));

    if (this._clearBtn) this._clearBtn.addEventListener("click", this._boundClearClick);
    this._syncClearButtonState();

    if (this.hasAttribute("disabled")) this._containerEl.setAttribute("tabindex", "-1");
  }

  /** 卸载：移除事件监听并拆卸 portal */
  disconnectedCallback() {
    this._containerEl.removeEventListener("click", this._boundTriggerClick);
    this._containerEl.removeEventListener("keydown", this._boundKeydown);
    if (this._clearBtn) this._clearBtn.removeEventListener("click", this._boundClearClick);
    document.removeEventListener("pointerdown", this._boundCloseOnPointerDownOutside, true);
    document.removeEventListener("click", this._boundCloseOnClickOutside);
    this._teardownPortal();
  }

  static get observedAttributes() {
    return ["placeholder", "value", "disabled", "required", "float-label", "options", "exclude-values", "error-message", "searchable", "show-arrow", "search-placeholder", "search-aria-label", "empty-message", "max-height", "default-value", "portal", "size", "disable-adaptation", "accordion", "clearable", "clear-aria-label"];
  }

  /** 空 size → medium；非空则经 `_normalizeSize` 纠正非法值 */
  _syncSizeAttribute() {
    const raw = this.getAttribute("size");
    if (raw === null || String(raw).trim() === "") {
      this.setAttribute("size", "medium");
      return;
    }
    const n = RoSelect._normalizeSize(raw);
    if (String(raw).trim().toLowerCase() !== n) this.setAttribute("size", n);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case "size":
        this._syncSizeAttribute();
        break;
      case "placeholder":
      case "float-label":
        this._updateLabel();
        this._updateSearchPlaceholder();
        break;
      case "search-placeholder":
        this._updateSearchPlaceholder();
        break;
      case "search-aria-label":
        this._syncSearchAriaLabel();
        break;
      case "empty-message":
        if (this._panelEl) this._renderOptions();
        break;
      case "value":
        this._value = newValue != null ? newValue : "";
        this._updateDisplay();
        this._updateHasValueState();
        this._internals.setFormValue(this._value);
        if (this._panelEl) this._renderOptions();
        break;
      case "disabled":
        this._containerEl.setAttribute("tabindex", this.hasAttribute("disabled") ? "-1" : "0");
        this._syncClearButtonState();
        break;
      case "clearable":
      case "clear-aria-label":
        this._syncClearButtonState();
        break;
      case "options":
      case "exclude-values":
        this._buildOptions();
        if (this._panelEl) this._renderOptions();
        break;
      case "error-message":
        if (this._isError) this._errorEl.textContent = newValue || this._errorMessage;
        break;
    }
  }

  /**
   * 读取需要从子 <option> 中排除的 value；多值用逗号分隔。
   * 示例：exclude-values="---,placeholder"
   */
  _getExcludedOptionValues() {
    return new Set(
      String(this.getAttribute("exclude-values") || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
    );
  }

  /**
   * 是否为可参与选择与提交的有效 <option>。
   * 组件只处理通用规则；业务特殊占位值通过 exclude-values 显式传入。
   * @param {HTMLOptionElement} optionEl
   */
  _isSelectableOptionElement(optionEl) {
    if (!optionEl || optionEl.disabled) return false;
    const excludedValues = this._getExcludedOptionValues();
    const value = (optionEl.getAttribute("value") ?? optionEl.value ?? "").trim();
    if (!value || excludedValues.has(value)) return false;
    return true;
  }

  /** 当前 value 是否落在可选项列表中（仅叶子节点） */
  _hasSelectableValue(value) {
    const val = String(value ?? "").trim();
    if (!val) return false;
    const walk = (items) => {
      for (const item of items) {
        if (item.children && item.children.length) {
          if (walk(item.children)) return true;
        } else if (item.value === val) {
          return true;
        }
      }
      return false;
    };
    return walk(this._options);
  }

  /** 从子节点 <option> 或 options 属性（JSON）解析选项列表 */
  _buildOptions() {
    const fromChildren = this.querySelectorAll("option");
    if (fromChildren.length) {
      this._options = Array.from(fromChildren)
        .filter((o) => this._isSelectableOptionElement(o))
        .map((o) => ({
          value: o.getAttribute("value") ?? o.textContent.trim(),
          label: o.textContent.trim(),
          dataset: o.dataset ? { ...o.dataset } : {},
          class: o.getAttribute("class") || ""
        }));
      return;
    }
    const optsAttr = this.getAttribute("options");
    if (optsAttr) {
      try {
        const arr = JSON.parse(optsAttr);
        const parseOptions = (items) => {
          return Array.isArray(items) ? items.map((o) => ({
            value: String(o.value ?? o.id ?? ""),
            label: String(o.label ?? o.name ?? o.text ?? ""),
            dataset: (o && typeof o.dataset === "object") ? { ...o.dataset } : {},
            class: o.class || "",
            children: o.children ? parseOptions(o.children) : null
          })) : [];
        };
        this._options = parseOptions(arr);
      } catch (_) {
        this._options = [];
      }
      return;
    }
    this._options = [];
  }

  _updateLabel() {
    const placeholder = this.getAttribute("placeholder") || "";
    this._labelEl.textContent = placeholder;
  }

  _updateSearchPlaceholder() {
    if (!this._searchInputEl) return;
    this._searchInputEl.placeholder = this.getAttribute("search-placeholder") || this.getAttribute("placeholder") || "";
  }

  /** 下拉内搜索框无障碍标签：search-aria-label → RoComponentsLocale.select.search_aria_label → 英文兜底 */
  _syncSearchAriaLabel() {
    if (!this._searchInputEl) return;
    const attr = this.getAttribute("search-aria-label");
    const label =
      attr != null && String(attr).trim() !== ""
        ? attr
        : window.RoComponentsLocale?.select?.search_aria_label || "Search options";
    this._searchInputEl.setAttribute("aria-label", label);
  }

  /** 无匹配选项时的空态文案：empty-message → RoComponentsLocale.select.empty_message → 英文兜底 */
  _getEmptyMessageText() {
    const attr = this.getAttribute("empty-message");
    if (attr != null && String(attr).trim() !== "") return attr;
    if (window.RoComponentsLocale?.select?.empty_message) {
      return window.RoComponentsLocale.select.empty_message;
    }
    return "No Data Found";
  }

  /** 根据当前 value 更新展示文案，并同步选中项的 dataset 到 value 区域与 host */
  _updateDisplay() {
    const findOpt = (items, val) => {
      for (const item of items) {
        if (item.value === val && !item.children) return item;
        if (item.children) {
          const found = findOpt(item.children, val);
          if (found) return found;
        }
      }
      return null;
    };
    const opt = findOpt(this._options, this._value);
    this._valueEl.textContent = opt ? opt.label : "";
    this._syncValueElDataset(opt ? opt.dataset : null);
  }

  /** 将选中项的 dataset 同步到 .value-text 与 host 的 data-*，并移除已不存在的键 */
  _syncValueElDataset(dataset) {
    const data = dataset && typeof dataset === "object" ? dataset : {};
    const keys = Object.keys(data);
    const dataAttr = (k) => "data-" + k.replace(/([A-Z])/g, "-$1").toLowerCase();
    this._lastSyncedDatasetKeys.forEach((k) => this._valueEl.removeAttribute(dataAttr(k)));
    this._lastSyncedDatasetKeys.forEach((k) => this.removeAttribute(dataAttr(k)));
    keys.forEach((k) => { this._valueEl.dataset[k] = data[k]; });
    keys.forEach((k) => { this.dataset[k] = data[k]; });
    this._lastSyncedDatasetKeys = keys;
  }

  _updateHasValueState() {
    if (this._value) this.setAttribute("has-value", "");
    else this.removeAttribute("has-value");
    this._syncClearButtonState();
  }

  /** clearable 已开启（非 clearable="false"） */
  _isClearableEnabled() {
    return this.hasAttribute("clearable") && this.getAttribute("clearable") !== "false";
  }

  /**
   * 同步清除按钮：disabled、aria-label、aria-hidden（无清空资格时从辅助技术隐藏）。
   */
  _syncClearButtonState() {
    if (!this._clearBtn || !this._clearBtnWrap) return;
    const eligible =
      this._isClearableEnabled() && !!this._value && !this.hasAttribute("disabled");
    this._clearBtn.disabled = !eligible;
    const label =
      this.getAttribute("clear-aria-label") ||
      window.RoComponentsLocale?.select?.clear_aria_label ||
      "Clear selection";
    this._clearBtn.setAttribute("aria-label", label);
    if (eligible) {
      this._clearBtn.removeAttribute("aria-hidden");
      this._clearBtnWrap.removeAttribute("aria-hidden");
    } else {
      this._clearBtn.setAttribute("aria-hidden", "true");
      this._clearBtnWrap.setAttribute("aria-hidden", "true");
    }
  }

  _updateSlotVisibility(slotEl) {
    const nodes = slotEl.assignedNodes({ flatten: true });
    const hasContent = nodes.some((n) => n.nodeType === Node.ELEMENT_NODE);
    slotEl.classList.toggle("empty", !hasContent);
  }

  _getFilteredOptions() {
    if (!this._searchQuery) return this._options;
    const q = this._searchQuery.toLowerCase();

    const filterItems = (items) => {
      const result = [];
      items.forEach(item => {
        if (item.children) {
          const filteredChildren = filterItems(item.children);
          if (filteredChildren.length > 0) {
            result.push({ ...item, children: filteredChildren });
            // 搜索时自动展开匹配项的父级
            this._expandedKeys.add(item.label);
          } else if (item.label.toLowerCase().includes(q)) {
            // 如果父级匹配但子级不匹配，也保留（可选逻辑，根据需求决定）
            result.push({ ...item, children: [] });
          }
        } else if (item.label.toLowerCase().includes(q)) {
          result.push(item);
        }
      });
      return result;
    };

    return filterItems(this._options);
  }

  _renderOptions() {
    const filtered = this._getFilteredOptions();
    this._optionListEl.innerHTML = "";
    if (filtered.length === 0) {
      const empty = document.createElement("div");
      empty.className = "option-empty";
      empty.textContent = this._getEmptyMessageText();
      this._optionListEl.appendChild(empty);
      return;
    }

    // 计算最大深度以应用不同的垂直间距
    const getMaxDepth = (items) => {
      let max = 0;
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          max = Math.max(max, 1 + getMaxDepth(item.children));
        } else {
          max = Math.max(max, 1);
        }
      });
      return max;
    };
    const maxDepth = getMaxDepth(filtered);

    // 为面板添加深度类，以便 CSS 控制垂直间距
    this._panelEl.classList.remove("depth-1", "depth-2", "depth-3");
    if (maxDepth > 1) {
      this._panelEl.classList.add(`depth-${Math.min(maxDepth, 3)}`);
    }

    const isMultiLevel = maxDepth > 1;

    const renderList = (items, level = 1) => {
      items.forEach((opt) => {
        const item = document.createElement("div");
        item.className = "option-item";
        if (isMultiLevel) item.classList.add(`level-${level}`);
        if (opt.class) item.classList.add(...String(opt.class).split(/\s+/).filter(Boolean));

        const labelSpan = document.createElement("span");
        labelSpan.className = "option-label";
        labelSpan.textContent = opt.label;
        item.appendChild(labelSpan);

        if (opt.children && opt.children.length > 0) {
          item.classList.add("option-group-header");
          const isExpanded = this._expandedKeys.has(opt.label);
          if (isExpanded) item.classList.add("expanded");

          const arrow = document.createElement("span");
          arrow.className = "group-arrow";
          arrow.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
            </svg>
          `;
          item.appendChild(arrow);

          item.addEventListener("click", (e) => {
            e.stopPropagation();
            if (this._expandedKeys.has(opt.label)) {
              this._expandedKeys.delete(opt.label);
            } else {
              // 手风琴模式：展开当前项时，收起同层级的其他项
              if (this.hasAttribute("accordion")) {
                items.forEach(sibling => {
                  if (sibling.children && sibling.label !== opt.label) {
                    this._expandedKeys.delete(sibling.label);
                  }
                });
              }
              this._expandedKeys.add(opt.label);
            }
            this._renderOptions();
          });

          this._optionListEl.appendChild(item);
          if (isExpanded) {
            renderList(opt.children, level + 1);
          }
        } else {
          item.setAttribute("role", "option");
          item.setAttribute("aria-selected", opt.value === this._value ? "true" : "false");
          item.dataset.value = opt.value;
          if (opt.dataset) {
            Object.keys(opt.dataset).forEach((k) => { item.dataset[k] = opt.dataset[k]; });
          }
          item.setAttribute("tabindex", "0");
          if (opt.value === this._value) item.classList.add("selected");

          item.addEventListener("click", (e) => {
            e.stopPropagation();
            this._select(opt.value);
          });
          this._optionListEl.appendChild(item);
        }
      });
    };

    renderList(filtered);
  }

  _onTriggerClick(e) {
    if (this.hasAttribute("disabled")) return;
    if (e && e.target === this._searchInputEl) return;
    if (this._isOpen) this._close();
    else {
      this._openViaKeyboard = false;
      this._open();
    }
  }

  /** 展开下拉：决定上下方向、应用面板最大高度、可选 portal 挂载并定位（在 ro-dialog 内按原生 dialog 盒体算上下余量，避免误判 expand-up 导致贴边裁切） */
  _open() {
    this._buildOptions();
    this._isOpen = true;
    const rect = this._containerEl.getBoundingClientRect();
    const EDGE_GAP = 8;
    const MIN_SIDE_PX = 80;
    const roDialog = this.closest("ro-dialog");
    const dialogEl = roDialog?.shadowRoot?.querySelector("dialog");
    let spaceBelow;
    let spaceAbove;
    if (dialogEl) {
      const d = dialogEl.getBoundingClientRect();
      spaceBelow = Math.max(0, d.bottom - rect.bottom - EDGE_GAP);
      spaceAbove = Math.max(0, rect.top - d.top - EDGE_GAP);
    } else {
      spaceBelow = window.innerHeight - rect.bottom;
      spaceAbove = rect.top;
    }
    let expandUp = spaceAbove >= spaceBelow;
    if (expandUp && spaceAbove < MIN_SIDE_PX && spaceBelow > spaceAbove) {
      expandUp = false;
    } else if (!expandUp && spaceBelow < MIN_SIDE_PX && spaceAbove > spaceBelow) {
      expandUp = true;
    }
    if (expandUp) {
      this.setAttribute("expand-up", "");
    } else {
      this.removeAttribute("expand-up");
    }
    this._applyPanelMaxHeight(rect);
    const usePortal = this.getAttribute("portal") !== "false";
    if (usePortal && this._panelEl) {
      this._portalRoot = document.createElement("div");
      this._portalRoot.className = "ro-select-portal";
      if (this.getAttribute("searchable") === "false") this._portalRoot.classList.add("no-search");
      this._panelEl.parentNode.removeChild(this._panelEl);
      this._portalRoot.appendChild(this._panelEl);
      const portalContainer = dialogEl || document.body;
      if (portalContainer !== document.body) this._portalRoot.classList.add("ro-select-portal-in-dialog");
      this._injectPortalStyles(portalContainer);
      portalContainer.appendChild(this._portalRoot);
      this._portalUsed = true;
      this._updatePortalPosition();
      window.addEventListener("scroll", this._boundUpdatePortalPosition, true);
      window.addEventListener("resize", this._boundUpdatePortalPosition);
    }
    this.setAttribute("focused", "");
    this._containerEl.setAttribute("aria-expanded", "true");
    this._searchQuery = "";
    if (this._searchInputEl) this._searchInputEl.value = "";
    this._renderOptions();
    requestAnimationFrame(() => {
      document.addEventListener("pointerdown", this._boundCloseOnPointerDownOutside, true);
      document.addEventListener("click", this._boundCloseOnClickOutside);
      if (this._searchInputEl && this.getAttribute("searchable") !== "false") {
        this._focusSearchInput(this._openViaKeyboard);
        this._openViaKeyboard = false;
      }
    });
  }

  /**
   * 聚焦下拉内搜索框：鼠标点开时不显示聚焦环（仍可立即输入），键盘展开或 Tab 进入时保留可见焦点。
   * @param {boolean} focusVisible 是否显示浏览器/样式的 focus 指示
   */
  _focusSearchInput(focusVisible) {
    if (!this._searchInputEl) return;
    try {
      this._searchInputEl.focus({ focusVisible: !!focusVisible });
    } catch {
      this._searchInputEl.focus();
    }
  }

  _injectPortalStyles(portalContainer) {
    const root = portalContainer === document.body ? document : portalContainer.getRootNode();
    const existing = root.querySelector ? root.querySelector("#ro-select-portal-styles") : null;
    if (existing) return;
    const style = document.createElement("style");
    style.id = "ro-select-portal-styles";
    style.textContent = RoSelect.PORTAL_STYLES;
    if (root === document) {
      (document.head || document.documentElement).appendChild(style);
    } else {
      root.appendChild(style);
    }
  }

  /** 根据触发器位置与 expand-up 更新 portal 内面板的 left/top 或 bottom/width */
  _updatePortalPosition() {
    if (!this._portalRoot || !this._panelEl || !this._containerEl) return;
    const rect = this._containerEl.getBoundingClientRect();
    const panel = this._panelEl;
    const container = this._portalRoot.parentNode;
    const inDialog = container && container !== document.body;
    const containerRect = inDialog ? container.getBoundingClientRect() : null;
    panel.style.width = rect.width + "px";
    panel.style.left = inDialog ? (rect.left - containerRect.left) + "px" : rect.left + "px";
    const gap = 8;
    if (this.hasAttribute("expand-up")) {
      panel.style.top = "";
      panel.style.bottom = (inDialog ? containerRect.bottom - rect.top + gap : window.innerHeight - rect.top + gap) + "px";
    } else {
      panel.style.bottom = "";
      panel.style.top = (inDialog ? rect.bottom - containerRect.top + gap : rect.bottom + gap) + "px";
    }
  }

  /** 拆卸 portal：将面板移回 shadow，移除容器与滚动/resize 监听 */
  _teardownPortal() {
    if (!this._portalUsed || !this._portalRoot || !this._panelEl) return;
    ["left", "top", "bottom", "width"].forEach((k) => this._panelEl.style.removeProperty(k));
    this._portalRoot.removeChild(this._panelEl);
    this.shadowRoot.insertBefore(this._panelEl, this._errorEl);
    if (this._portalRoot.parentNode) this._portalRoot.parentNode.removeChild(this._portalRoot);
    window.removeEventListener("scroll", this._boundUpdatePortalPosition, true);
    window.removeEventListener("resize", this._boundUpdatePortalPosition);
    this._portalRoot = null;
    this._portalUsed = false;
  }

  /** 根据 max-height 属性设置面板最大高度：未设默认 280px；为 0 时按剩余空间计算 */
  _applyPanelMaxHeight(rect) {
    if (!this._panelEl) return;
    const attr = this.getAttribute("max-height");
    if (attr === null || attr === "") {
      this._panelEl.style.maxHeight = "";
      return;
    }
    const num = parseInt(attr, 10);

    if (num === 0) {
      /** 弹窗内面板与 dialog 边界的留白（避免向上展开时顶满可见上沿） */
      const DIALOG_PANEL_EDGE = 38;
      const VIEWPORT_EDGE = 20;
      const roDialog = this.closest("ro-dialog");
      const dialogEl = roDialog?.shadowRoot?.querySelector("dialog");
      let space;
      if (dialogEl) {
        const containerRect = dialogEl.getBoundingClientRect();
        space = this.hasAttribute("expand-up")
          ? rect.top - containerRect.top - DIALOG_PANEL_EDGE
          : containerRect.bottom - rect.bottom - 4 - DIALOG_PANEL_EDGE;
      } else {
        space = this.hasAttribute("expand-up")
          ? rect.top - VIEWPORT_EDGE
          : window.innerHeight - rect.bottom - 4 - VIEWPORT_EDGE;
      }
      this._panelEl.style.maxHeight = Math.max(100, space) + "px";
    } else if (!isNaN(num) && num > 0) {
      this._panelEl.style.maxHeight = num + "px";
    } else {
      this._panelEl.style.maxHeight = "280px";
    }
  }

  /** 关闭下拉：拆卸 portal、移除外部点击监听、可选校验并派发 blur */
  _close() {
    this._isOpen = false;
    this._teardownPortal();
    this.removeAttribute("focused");
    this.removeAttribute("expand-up");
    this._containerEl.setAttribute("aria-expanded", "false");
    document.removeEventListener("pointerdown", this._boundCloseOnPointerDownOutside, true);
    document.removeEventListener("click", this._boundCloseOnClickOutside);
    if (!this.hasAttribute("validate-on-change")) this.validate();
    this.dispatchEvent(new CustomEvent("blur", { bubbles: true, detail: { value: this._value } }));
  }

  _closeOnClickOutside(e) {
    const path = e.composedPath();
    if (path.includes(this._panelEl) || path.includes(this)) return;
    this._close();
  }

  /**
   * 清空当前选中（不恢复 default-value）：状态更新顺序对齐 _select——必要时先 _close（含 blur 与非 validate-on-change 的 validate），再派发 change，再按需 validate-on-change。
   */
  _clearSelection(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!this._isClearableEnabled()) return;
    if (this.hasAttribute("disabled")) return;
    if (!this._value) return;

    this._value = "";
    this.removeAttribute("value");
    this._updateDisplay();
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    if (this._panelEl) this._renderOptions();

    if (this._isOpen) {
      this._close();
    } else if (!this.hasAttribute("validate-on-change")) {
      this.validate();
    }

    this.dispatchEvent(new CustomEvent("change", { bubbles: true, detail: { value: this._value } }));
    if (this.hasAttribute("validate-on-change")) this.validate();
  }

  /** 选中选项：更新 value、展示、表单值并派发 change；若 validate-on-change 则校验 */
  _select(value) {
    this._value = value;
    this.setAttribute("value", value);
    this._updateDisplay();
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
    this._close();
    this.dispatchEvent(new CustomEvent("change", { bubbles: true, detail: { value: this._value } }));
    if (this.hasAttribute("validate-on-change")) this.validate();
  }

  _focusNextOption(delta) {
    const items = Array.from(this._optionListEl.querySelectorAll(".option-item"));
    if (!items.length) return;
    let idx = items.findIndex((el) => el.classList.contains("selected"));
    if (idx < 0) idx = 0;
    idx = Math.max(0, Math.min(items.length - 1, idx + delta));
    items[idx].focus();
  }

  _onKeydown(e) {
    if (this.hasAttribute("disabled")) return;
    if (!this._isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this._openViaKeyboard = true;
        this._open();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this._close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this._focusNextOption(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      this._focusNextOption(-1);
      return;
    }
    if (e.key === "Enter") {
      const selected = this._optionListEl.querySelector(".option-item.selected");
      if (selected) {
        e.preventDefault();
        this._select(selected.dataset.value);
      }
    }
  }

  /** 必填校验：required 且无值时报错，更新 error 状态与 ARIA，派发 validate/error 事件 */
  validate() {
    if (this.hasAttribute("disabled")) return { valid: true, message: "" };
    let errorMessage = "";
    const value = String(this._value ?? "").trim();
    if (this.hasAttribute("required") && !value) {
      errorMessage =
        this.getAttribute("error-message") ||
        window.RoFormLocale?.required ||
        "This field is required";
    } else if (
      this.hasAttribute("required") &&
      value &&
      this._options.length > 0 &&
      !this._hasSelectableValue(value)
    ) {
      errorMessage =
        this.getAttribute("error-message") ||
        window.RoFormLocale?.required ||
        "This field is required";
    }
    const valid = !errorMessage;
    this._isError = !valid;
    this._errorMessage = errorMessage;
    if (this._isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || errorMessage;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
    this._containerEl.setAttribute("aria-invalid", this._isError ? "true" : "false");
    this.dispatchEvent(new CustomEvent("validate", { bubbles: true, detail: { valid, message: errorMessage, value: this._value } }));
    if (!valid) this.dispatchEvent(new CustomEvent("error", { bubbles: true, detail: { message: errorMessage, value: this._value } }));
    return { valid, message: errorMessage };
  }

  /** 重置为 default-value，若无则清空 value，并清除错误状态 */
  reset() {
    const def = this._getDefaultValueAttr();
    if (def !== "") {
      this.setAttribute("value", def);
    } else {
      this._value = "";
      this.removeAttribute("value");
    }
    this._setError(false, "");
    this._updateDisplay();
    this._updateHasValueState();
    this._internals.setFormValue(this._value);
  }

  clearError() {
    this._setError(false, "");
  }

  focus() {
    this._containerEl.focus();
  }

  /** 内部：设置校验错误状态并同步到 host 的 error 属性与 .error-message、aria-invalid */
  _setError(isError, message) {
    this._isError = isError;
    this._errorMessage = message;
    if (isError) {
      this.setAttribute("error", "");
      this._errorEl.textContent = this.getAttribute("error-message") || message;
    } else {
      this.removeAttribute("error");
      this._errorEl.textContent = "";
    }
    this._containerEl.setAttribute("aria-invalid", isError ? "true" : "false");
  }

  get value() {
    return this._value;
  }
  set value(val) {
    this.setAttribute("value", val != null ? String(val) : "");
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(val) {
    if (val) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }
  set required(val) {
    if (val) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  /** 规范后档位：large | medium | small（无属性视为 large） */
  get size() {
    return RoSelect._normalizeSize(this.getAttribute("size"));
  }
  set size(val) {
    if (val == null || val === "") this.removeAttribute("size");
    else this.setAttribute("size", String(val));
  }

  /** 程序化清空选中，行为与点击清除按钮一致（需启用 clearable） */
  clearValue() {
    this._clearSelection(null);
  }

  formResetCallback() {
    this.value = "";
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }
}

customElements.define("ro-select", RoSelect);
